// Lote UX Leyro parity (iter-2) — refactor de ProfilePage en 5 seccións
// con sidebar nav (anchors). Sobre o esquema Phase 25 iter-3 (Airbnb-style
// cards), reorganízase a información por dominio:
//
//   1. Conta (datos persoais + fiscais + contrasinal + 2FA + dispositivos)
//   2. Roles e verificación (chips + acceso a /profile/roles + KYC)
//   3. Operativa do meu rol (SellerSetup, VetSetup, TransporterSetup, DDS)
//   4. Administración (só SILLEDA_ADMIN)
//   5. Sesión (logout)
//
// Sen rotas novas: RolesPage segue como detalle. PersonalInfoCard quédalle
// só nome/email; os botóns cross-rol (Xestionar roles, DDS, KYC review)
// móvense ás súas seccións naturais.

import { CheckCircle, FileText, Gavel, Lock, Mail, ShieldCheck, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { changePassword } from '../api/profile';
import { describeAxiosError } from '../api/errors';
import { getMyProfile, updateMyProfile } from '../api/users';
import {
  clearMyAvatar,
  requestAvatarUploadUrl,
  setMyAvatar,
  uploadAvatarToS3,
} from '../api/avatar';
import InfoCallout from '../components/InfoCallout';
import Avatar from '../components/ui/Avatar';
import Spinner from '../components/ui/Spinner';
import { DevicesList } from '../components/profile/DevicesList';
import { SellerSetupCard } from '../components/profile/SellerSetupCard';
import { TransporterSetupCard } from '../components/profile/TransporterSetupCard';
import { TwoFactorSetup } from '../components/profile/TwoFactorSetup';
import { VetSetupCard } from '../components/profile/VetSetupCard';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useKycStatus } from '../hooks/useKycStatus';

// ─── helpers ─────────────────────────────────────────────────────────────────

function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-border rounded-2xl p-6 bg-white">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="font-semibold text-dark text-base">{title}</h3>
      </div>
      {children}
    </section>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function ProfilePage(): JSX.Element {
  const { user, accessToken, logout } = useAuthContext();
  const { addToast } = useToast();

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/profile" replace />;
  }

  if (!user) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-10">
        <Spinner size={28} />
      </main>
    );
  }

  const isAdmin = user.primary_role === 'SILLEDA_ADMIN';
  const hasRoleOps =
    user.roles.includes('FARMER') ||
    user.roles.includes('VET') ||
    user.roles.includes('TRANSPORTISTA') ||
    user.roles.includes('BUYER');

  // Anchors da sidebar nav. As seccións renderízanse coas mesmas ids para que
  // o scroll-to (CSS smooth-scroll) funcione.
  const navSections: { id: string; label: string; show: boolean }[] = [
    { id: 'conta', label: 'A miña conta', show: true },
    { id: 'roles', label: 'Roles e verificación', show: true },
    { id: 'operativa', label: 'Operativa do meu rol', show: hasRoleOps },
    { id: 'admin', label: 'Administración', show: isAdmin },
    { id: 'sesion', label: 'Sesión', show: true },
  ];

  return (
    <main className="max-w-5xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-bold text-dark mb-8">Perfil</h1>

      <div className="grid md:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar */}
        <aside>
          <div className="border border-border rounded-3xl p-6 text-center sticky top-24 bg-white">
            <AvatarUploader />
            <h2 className="text-xl font-bold text-dark">{user.name}</h2>
            <p className="text-sm text-muted mt-1">
              {user.primary_role
                ? user.primary_role[0] + user.primary_role.slice(1).toLowerCase()
                : 'Conta'}
            </p>

            <div className="mt-6 space-y-2 text-left">
              <div className="flex items-center gap-2 text-sm">
                {user.email_verified ? (
                  <CheckCircle size={16} className="text-rauxa" />
                ) : (
                  <XCircle size={16} className="text-amber-600" />
                )}
                <span className={user.email_verified ? 'text-dark' : 'text-muted'}>
                  {user.email_verified ? 'Email verificado' : 'Email sen verificar'}
                </span>
              </div>
              <KycSidebarRow />
            </div>

            {/* Nav por anchors. Browsers nativos seguen scroll smooth se o
                tag <html> ten `scroll-behavior: smooth` (xa está no index.css
                vía Tailwind base). */}
            <nav className="mt-6 pt-6 border-t border-border text-left space-y-1">
              {navSections
                .filter((s) => s.show)
                .map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-sm text-dark hover:text-rauxa hover:bg-subtle rounded-lg px-3 py-2 transition-colors"
                  >
                    {s.label}
                  </a>
                ))}
            </nav>
          </div>
        </aside>

        {/* Main column */}
        <div className="space-y-10">
          {/* ── 1. A miña conta ────────────────────────────────────────── */}
          <section id="conta" className="scroll-mt-24 space-y-6">
            <h2 className="text-xl font-bold text-dark border-b border-border pb-2">
              A miña conta
            </h2>
            <PersonalInfoCard />
            {/* Lote V-7 Fase B — datos fiscais para contrato compraventa. */}
            <FiscalDataCard />
            <PasswordCard />
            <Card
              title="Verificación en dúas etapas (2FA)"
              icon={<ShieldCheck size={18} className="text-muted" />}
            >
              <TwoFactorSetup />
            </Card>
            <Card
              title="Dispositivos conectados"
              icon={<ShieldCheck size={18} className="text-muted" />}
            >
              <DevicesList />
            </Card>
          </section>

          {/* ── 2. Roles e verificación ────────────────────────────────── */}
          <section id="roles" className="scroll-mt-24 space-y-6">
            <h2 className="text-xl font-bold text-dark border-b border-border pb-2">
              Roles e verificación
            </h2>
            <RolesSummaryCard />
            <KycStatusCard />
          </section>

          {/* ── 3. Operativa do meu rol ────────────────────────────────── */}
          {hasRoleOps && (
            <section id="operativa" className="scroll-mt-24 space-y-6">
              <h2 className="text-xl font-bold text-dark border-b border-border pb-2">
                Operativa do meu rol
              </h2>
              {user.roles.includes('FARMER') && <SellerSetupCard />}
              {user.roles.includes('VET') && <VetSetupCard />}
              {user.roles.includes('TRANSPORTISTA') && <TransporterSetupCard />}
              {user.roles.includes('FARMER') && (
                <Card title="Vender animais" icon={<Gavel size={18} className="text-muted" />}>
                  <p className="text-sm text-muted mb-3 leading-relaxed">
                    A configuración da venda (prezo fixo ou subasta, datas, prezo de arranque) faise
                    dende a <strong>app móbil</strong>: seleccionas o animal na túa explotación e
                    premes <em>Vender</em>.
                  </p>
                  <Link to="/marketplace/vender" className="btn-outline inline-flex">
                    Como funciona →
                  </Link>
                </Card>
              )}
              {user.roles.includes('BUYER') && (
                <Card
                  title="DDS recibidas (EUDR)"
                  icon={<FileText size={18} className="text-muted" />}
                >
                  <p className="text-sm text-muted mb-3 leading-relaxed">
                    Aquí podes consultar e descargar as Declaracións de Dilixencia Debida (DDS)
                    asociadas ás túas compras conforme ao Regulamento UE 2023/1115.
                  </p>
                  <Link to="/marketplace/me/eudr" className="btn-outline inline-flex">
                    Ver DDS recibidas →
                  </Link>
                </Card>
              )}
            </section>
          )}

          {/* ── 4. Administración (só SILLEDA_ADMIN) ───────────────────── */}
          {isAdmin && (
            <section id="admin" className="scroll-mt-24 space-y-6">
              <h2 className="text-xl font-bold text-dark border-b border-border pb-2">
                Administración
              </h2>
              <Card title="Revisión KYC" icon={<ShieldCheck size={18} className="text-muted" />}>
                <p className="text-sm text-muted mb-3 leading-relaxed">
                  Revisa as solicitudes KYC pendentes (DNI + autoselfie) enviadas a través de DIDIT.
                  Aproba ou rexeita coa documentación adxunta.
                </p>
                <Link to="/marketplace/admin/kyc-review" className="btn-outline inline-flex">
                  Abrir panel de revisión →
                </Link>
              </Card>
            </section>
          )}

          {/* ── 5. Sesión ──────────────────────────────────────────────── */}
          <section id="sesion" className="scroll-mt-24 space-y-6">
            <h2 className="text-xl font-bold text-dark border-b border-border pb-2">Sesión</h2>
            <Card title="Pechar sesión">
              <p className="text-sm text-muted mb-3">
                Pechar a sesión en este dispositivo non afecta ás outras sesións. Para revogalas
                individualmente úsao a lista de <em>Dispositivos conectados</em> arriba.
              </p>
              <button
                type="button"
                onClick={() => {
                  void logout();
                  addToast('Sesión pechada.', 'default');
                }}
                className="btn-outline"
              >
                Pechar sesión
              </button>
            </Card>
          </section>
        </div>
      </div>
    </main>
  );
}

// ─── Avatar uploader (sidebar) ──────────────────────────────────────────────

function AvatarUploader() {
  const { user, refreshUser } = useAuthContext();
  const { addToast } = useToast();
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reseleccionar mesmo ficheiro
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      addToast('Formato non soportado. Usa JPG, PNG ou WebP.', 'error');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      addToast('A imaxe supera 2 MB. Recórtaa antes de subila.', 'error');
      return;
    }
    setBusy(true);
    try {
      const presign = await requestAvatarUploadUrl({
        content_length: file.size,
        content_type: file.type as 'image/jpeg' | 'image/png' | 'image/webp',
      });
      await uploadAvatarToS3(presign.upload_url, file, file.type);
      await setMyAvatar(presign.public_url);
      await refreshUser();
      addToast('Avatar actualizado.', 'success');
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido actualizar o avatar.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    setBusy(true);
    try {
      await clearMyAvatar();
      await refreshUser();
      addToast('Avatar eliminado.', 'default');
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido eliminar.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto mb-4">
      <div className="relative w-20 h-20 mx-auto">
        <Avatar user={user} size={80} />
        <label
          className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-rauxa text-white text-xs font-bold flex items-center justify-center cursor-pointer hover:opacity-90 ${
            busy ? 'opacity-60' : ''
          }`}
          title="Cambiar avatar"
        >
          {busy ? <Spinner size={14} /> : '✎'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            disabled={busy}
            className="hidden"
          />
        </label>
      </div>
      {user.avatar_url && !busy && (
        <button
          type="button"
          onClick={() => void handleClear()}
          className="block mt-2 mx-auto text-xs text-muted hover:text-danger"
        >
          Eliminar avatar
        </button>
      )}
    </div>
  );
}

// ─── KYC row (sidebar) ──────────────────────────────────────────────────────

function KycSidebarRow() {
  const { status } = useKycStatus();
  let label = 'KYC sen iniciar';
  let icon = <ShieldCheck size={16} className="text-muted" />;
  let toneClass = 'text-muted';
  if (status?.status === 'VERIFIED') {
    label = 'KYC verificado';
    icon = <CheckCircle size={16} className="text-rauxa" />;
    toneClass = 'text-dark';
  } else if (status?.status === 'REJECTED') {
    label = 'KYC rexeitado';
    icon = <XCircle size={16} className="text-danger" />;
    toneClass = 'text-danger';
  } else if (status && (status.status === 'PENDING' || status.status === 'SUBMITTED')) {
    label = 'KYC en revisión';
    icon = <ShieldCheck size={16} className="text-amber-600" />;
    toneClass = 'text-amber-700';
  }
  return (
    <Link
      to="/marketplace/kyc"
      className="flex items-center gap-2 text-sm hover:underline"
      title="Ver detalle KYC"
    >
      {icon}
      <span className={toneClass}>{label}</span>
    </Link>
  );
}

// ─── personal info card ─────────────────────────────────────────────────────

function PersonalInfoCard() {
  const { user, refreshUser } = useAuthContext();
  const { addToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  function startEdit() {
    setName(user?.name ?? '');
    setError(null);
    setEditing(true);
  }

  async function handleSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) {
      setError('O nome non pode quedar baleiro.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateMyProfile({ name: name.trim() });
      await refreshUser();
      addToast('Datos persoais actualizados.', 'success');
      setEditing(false);
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido gardar.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Datos persoais">
      {!editing ? (
        <>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted font-medium mb-0.5">
                Nome
              </dt>
              <dd className="text-dark">{user.name}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted font-medium mb-0.5">
                Email
              </dt>
              <dd className="text-dark">{user.email}</dd>
            </div>
          </dl>
          <div className="mt-4 flex gap-3 flex-wrap">
            <button type="button" onClick={startEdit} className="btn-outline !py-2 !px-4 text-sm">
              Editar nome
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleSave} className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
              Nome completo
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="airbnb-input"
              autoFocus
            />
          </label>
          {error && <p className="text-xs text-rauxa">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={() => setEditing(false)} className="btn-outline flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <Spinner size={16} className="mx-auto" /> : 'Gardar'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}

// ─── password change card ───────────────────────────────────────────────────

function PasswordCard() {
  const { addToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setEditing(false);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (next.length < 8) {
      setError('O novo contrasinal debe ter polo menos 8 caracteres.');
      return;
    }
    if (next !== confirm) {
      setError('Os contrasinais non coinciden.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      addToast('Contrasinal actualizado correctamente.', 'success');
      reset();
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido actualizar o contrasinal.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Contrasinal" icon={<Lock size={18} className="text-muted" />}>
      {!editing ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted">
            O teu contrasinal é privado. Cámbiao se sospeitas que alguén o coñece.
          </p>
          <button type="button" onClick={() => setEditing(true)} className="btn-ghost">
            Cambiar
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field
            label="Contrasinal actual"
            value={current}
            onChange={setCurrent}
            type="password"
            autoComplete="current-password"
          />
          <Field
            label="Novo contrasinal"
            value={next}
            onChange={setNext}
            type="password"
            autoComplete="new-password"
          />
          <Field
            label="Confirmar novo contrasinal"
            value={confirm}
            onChange={setConfirm}
            type="password"
            autoComplete="new-password"
          />
          {error && (
            <p className="flex items-center gap-1.5 text-sm text-rauxa">
              <Mail size={14} />
              {error}
            </p>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={reset} className="btn-outline flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <Spinner size={16} className="mx-auto" /> : 'Gardar'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
}

function Field({ label, value, onChange, type = 'text', autoComplete }: FieldProps) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="airbnb-input"
      />
    </label>
  );
}

// ─── Datos fiscais (Lote V-7 Fase B) ────────────────────────────────────────
// Captura os campos novos esixidos polo contrato compraventa profesional
// (cláusula 1): NIF/CIF, domicilio fiscal e — só para PJ — representante
// legal. Sen estes campos o PDF do contrato renderiza "— (pendente)".

function FiscalDataCard(): JSX.Element {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state.
  const [entityType, setEntityType] = useState<'PERSOA_FISICA' | 'PERSOA_XURIDICA'>(
    'PERSOA_FISICA',
  );
  const [legalName, setLegalName] = useState('');
  const [nif, setNif] = useState('');
  const [taxLine1, setTaxLine1] = useState('');
  const [taxCity, setTaxCity] = useState('');
  const [taxPostal, setTaxPostal] = useState('');
  const [taxProvince, setTaxProvince] = useState('');
  const [legalRepName, setLegalRepName] = useState('');
  const [legalRepNif, setLegalRepNif] = useState('');
  const [legalRepRole, setLegalRepRole] = useState('');

  // Estado de "completitude" para o badge.
  const allFilled =
    nif.length > 0 &&
    legalName.length > 0 &&
    taxLine1.length > 0 &&
    taxCity.length > 0 &&
    taxPostal.length > 0 &&
    taxProvince.length > 0 &&
    (entityType !== 'PERSOA_XURIDICA' ||
      (legalRepName.length > 0 && legalRepNif.length > 0 && legalRepRole.length > 0));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMyProfile()
      .then((p) => {
        if (!alive) return;
        setEntityType(p.entity_type);
        setLegalName(p.legal_name ?? '');
        setNif(p.nif ?? '');
        setTaxLine1(p.tax_address_line1 ?? '');
        setTaxCity(p.tax_address_city ?? '');
        setTaxPostal(p.tax_address_postal ?? '');
        setTaxProvince(p.tax_address_province ?? '');
        setLegalRepName(p.legal_representative_name ?? '');
        setLegalRepNif(p.legal_representative_nif ?? '');
        setLegalRepRole(p.legal_representative_role ?? '');
      })
      .catch((err: unknown) => setError(describeAxiosError(err, 'Non se puido cargar o perfil.')))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function handleSave(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await updateMyProfile({
        entity_type: entityType,
        legal_name: legalName || undefined,
        nif: nif || undefined,
        tax_address_line1: taxLine1 || undefined,
        tax_address_city: taxCity || undefined,
        tax_address_postal: taxPostal || undefined,
        tax_address_province: taxProvince || undefined,
        legal_representative_name:
          entityType === 'PERSOA_XURIDICA' ? legalRepName || undefined : undefined,
        legal_representative_nif:
          entityType === 'PERSOA_XURIDICA' ? legalRepNif || undefined : undefined,
        legal_representative_role:
          entityType === 'PERSOA_XURIDICA' ? legalRepRole || undefined : undefined,
      });
      addToast('Datos fiscais actualizados.', 'success');
      setEditing(false);
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido gardar.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Datos fiscais" icon={<FileText size={18} className="text-muted" />}>
      <div className="mb-4">
        <InfoCallout
          storageKey="profile-fiscal-data-why"
          defaultOpen={true}
          icon={<FileText size={18} className="text-rauxa" />}
          title="Por que precisamos estes datos?"
          body="Aparecerán nos contratos de compraventa que asines en LeyroChain. NIF, razón social e domicilio fiscal son obrigatorios pola lexislación eIDAS de firma electrónica e por fiscalidade. Os datos do representante legal só se piden a persoas xurídicas."
        />
      </div>
      {loading ? (
        <Spinner size={20} />
      ) : !editing ? (
        <>
          <p className="text-sm text-muted mb-3">
            Estes datos aparecen no contrato de compravenda. Sen eles, as ventas no marketplace
            renderizan o contrato con campos en branco e poden non ser válidas legalmente.
          </p>
          <dl className="text-sm space-y-2">
            <Row label="Tipo">
              {entityType === 'PERSOA_XURIDICA' ? 'Persoa xurídica' : 'Persoa física'}
            </Row>
            <Row label="Nome / Razón social">{legalName || '—'}</Row>
            <Row label="NIF / CIF">{nif || '—'}</Row>
            <Row label="Domicilio fiscal">
              {[taxLine1, taxCity, taxPostal, taxProvince].filter(Boolean).join(', ') || '—'}
            </Row>
            {entityType === 'PERSOA_XURIDICA' && (
              <>
                <Row label="Representante legal">{legalRepName || '—'}</Row>
                <Row label="NIF representante">{legalRepNif || '—'}</Row>
                <Row label="Cargo">{legalRepRole || '—'}</Row>
              </>
            )}
          </dl>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-outline !py-2 !px-4 text-sm"
            >
              Editar datos fiscais
            </button>
            {!allFilled && (
              <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                ⚠ Faltan datos para o contrato
              </span>
            )}
          </div>
        </>
      ) : (
        <form onSubmit={handleSave} className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
              Tipo de entidade
            </span>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as 'PERSOA_FISICA' | 'PERSOA_XURIDICA')}
              className="airbnb-input"
            >
              <option value="PERSOA_FISICA">Persoa física</option>
              <option value="PERSOA_XURIDICA">Persoa xurídica (empresa)</option>
            </select>
          </label>
          <Field label="Nome / Razón social" value={legalName} onChange={setLegalName} />
          <Field label="NIF / CIF" value={nif} onChange={setNif} autoComplete="off" />
          <Field
            label="Dirección fiscal (rúa, número, portal)"
            value={taxLine1}
            onChange={setTaxLine1}
            autoComplete="street-address"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Cidade"
              value={taxCity}
              onChange={setTaxCity}
              autoComplete="address-level2"
            />
            <Field
              label="Código postal"
              value={taxPostal}
              onChange={setTaxPostal}
              autoComplete="postal-code"
            />
          </div>
          <Field
            label="Provincia"
            value={taxProvince}
            onChange={setTaxProvince}
            autoComplete="address-level1"
          />
          {entityType === 'PERSOA_XURIDICA' && (
            <>
              <p className="text-xs uppercase tracking-wide text-muted font-medium mt-2">
                Representante legal
              </p>
              <Field
                label="Nome completo do representante"
                value={legalRepName}
                onChange={setLegalRepName}
              />
              <Field label="NIF do representante" value={legalRepNif} onChange={setLegalRepNif} />
              <Field
                label="Cargo (ex. Administrador único)"
                value={legalRepRole}
                onChange={setLegalRepRole}
              />
            </>
          )}
          {error && <p className="text-xs text-rauxa">{error}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="btn-outline flex-1"
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <Spinner size={16} className="mx-auto" /> : 'Gardar'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-3 border-b border-border/60 pb-2">
      <dt className="text-muted">{label}</dt>
      <dd className="text-dark font-medium text-right">{children}</dd>
    </div>
  );
}

// ─── Roles summary card (sección 2) ──────────────────────────────────────────
// Mostra a lista de roles activos como chips, marca o primario, e expón un
// único botón "Engadir/Xestionar roles" que leva ao detalle (/profile/roles).
// Substitúe os tres botóns dispersos que estaban dentro de PersonalInfoCard.

function RolesSummaryCard(): JSX.Element | null {
  const { user } = useAuthContext();
  if (!user) return null;
  return (
    <Card title="Os meus roles" icon={<ShieldCheck size={18} className="text-muted" />}>
      <div className="flex flex-wrap gap-2 mb-4">
        {user.roles.map((r) => {
          const isPrimary = r === user.primary_role;
          return (
            <span
              key={r}
              className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                isPrimary ? 'bg-rauxa text-white' : 'bg-subtle text-dark border border-border'
              }`}
              title={isPrimary ? 'Rol primario' : 'Rol activo'}
            >
              {r}
              {isPrimary && <span className="ml-1 opacity-80">· primario</span>}
            </span>
          );
        })}
      </div>
      <p className="text-sm text-muted mb-3 leading-relaxed">
        Engade ou desactiva roles, ou cambia cal é o teu rol primario. Cada rol pode requirir
        verificacións específicas (ex. KYC, licenza veterinaria).
      </p>
      <Link to="/marketplace/profile/roles" className="btn-outline inline-flex">
        Engadir / Xestionar roles →
      </Link>
    </Card>
  );
}

// ─── KYC status card (sección 2) ─────────────────────────────────────────────
// Versión grande do estado KYC (a sidebar segue mostrando a línea pequena).
// O detalle vive en /marketplace/kyc — aquí só damos un resumo accionable.

function KycStatusCard(): JSX.Element {
  const { status } = useKycStatus();
  let title = 'Verificación de identidade (KYC)';
  let body = 'Antes de mercar ou vender precisamos confirmar a túa identidade vía DIDIT.';
  let cta = 'Iniciar verificación';
  let toneIcon = <ShieldCheck size={18} className="text-muted" />;
  if (status?.status === 'VERIFIED') {
    body = 'A túa identidade está verificada. Podes operar normalmente no marketplace.';
    cta = 'Ver detalle';
    toneIcon = <CheckCircle size={18} className="text-rauxa" />;
  } else if (status?.status === 'REJECTED') {
    body = 'A túa verificación foi rexeitada. Revisa a documentación e tenta de novo.';
    cta = 'Volver a tentar';
    toneIcon = <XCircle size={18} className="text-danger" />;
  } else if (status && (status.status === 'PENDING' || status.status === 'SUBMITTED')) {
    body = 'A túa verificación está en revisión. Recibirás un email cando se resolva.';
    cta = 'Ver estado';
    toneIcon = <ShieldCheck size={18} className="text-amber-600" />;
  }
  return (
    <Card title={title} icon={toneIcon}>
      <p className="text-sm text-muted mb-3 leading-relaxed">{body}</p>
      <Link to="/marketplace/kyc" className="btn-outline inline-flex">
        {cta} →
      </Link>
    </Card>
  );
}
